import {
    ForwardedRef,
    PropsWithChildren,
    forwardRef,
    useImperativeHandle,
    useRef,
} from "react";

import {
    styled,
    AlertColor,
    AlertPropsColorOverrides,
    Card,
    CardContent,
    Box,
    Alert,
    CardActions,
} from "@mui/material";
import { CommonProps } from "@mui/material/OverridableComponent";
import { OverridableStringUnion } from "@mui/types";

import { useAppSelector } from "app/hooks";
import {
    selectInFullscreenMode,
    selectInWindowMode,
} from "features/core/store/selectors";

export interface PlayerCardRef {
    root: HTMLDivElement | null;
}

export interface PlayerCardProps {
    marginFullScreenTop?: number;
    alertMessage?: string;
    alertSeverity?: OverridableStringUnion<
        AlertColor,
        AlertPropsColorOverrides
    >;
    cardActions?: React.ReactNode;
}

const PlayerCardComponent = forwardRef<
    PlayerCardRef,
    CommonProps & PropsWithChildren<PlayerCardProps>
>(
    (
        props: CommonProps & PropsWithChildren<PlayerCardProps>,
        ref: ForwardedRef<PlayerCardRef>,
    ) => {
        const rootRef = useRef<HTMLDivElement>(null);

        useImperativeHandle(ref, () => {
            return {
                root: rootRef.current,
            };
        }, []);

        const inWindowMode = useAppSelector(selectInWindowMode);
        const inFullscreenMode = useAppSelector(selectInFullscreenMode);

        return (
            <Card
                ref={rootRef}
                className={props.className}
                sx={{
                    ...(inFullscreenMode && {
                        borderRadius: 0,
                    }),
                }}
            >
                <CardContent
                    sx={{
                        ...(inWindowMode && {
                            height: 0, // maintain a specific aspect ratio.
                            padding: 0,
                            paddingTop: "56.25%", // ratio 16:9 of the videos we receive
                            position: "relative",
                            overflow: "hidden",
                        }),
                        ...(inFullscreenMode && {
                            padding: 0,
                            [`&:last-child`]: {
                                paddingBottom: 0,
                                lineHeight: 0,
                            },
                        }),
                    }}
                >
                    <Box
                        onContextMenu={(event) => event.preventDefault()}
                        sx={{
                            ...(inWindowMode && {
                                top: 0,
                                left: 0,
                                position: "absolute",

                                width: "100%",
                                height: "100%",
                            }),
                            ...(inFullscreenMode && {
                                position: "fixed",
                                left: 0,
                                height: `calc(100% - ${props.marginFullScreenTop || 0}px)`,
                                width: "100%",
                            }),
                        }}
                    >
                        {props.alertMessage && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    width: "100%",
                                    zIndex: (theme) => theme.zIndex.appBar,
                                }}
                            >
                                <Alert
                                    severity={props.alertSeverity || "info"}
                                    sx={{
                                        margin: 1,
                                    }}
                                >
                                    {props.alertMessage}
                                </Alert>
                            </Box>
                        )}

                        {props.children}
                    </Box>
                </CardContent>
                {inWindowMode && (
                    <CardActions
                        disableSpacing
                        sx={{
                            position: "relative",
                            height: 40,
                            padding: 0,
                        }}
                    >
                        {props.cardActions}
                    </CardActions>
                )}
            </Card>
        );
    },
);

export const PlayerCard = styled(PlayerCardComponent)(() => ({}));
